import { useNavigate } from '@tanstack/react-router';
import { MoreHorizontal } from 'lucide-react';

import { Button } from '@apps/web/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    TOAST_PAUSE_DURATION_MS,
    toastPauseStore,
} from '@apps/web/lib/toast_pause_store';
import { useNotificationPreferences } from '@apps/web/swr_data_fetching/data_fetching_hooks';

type ToastControlsMenuProps = {
    readonly organizationId: string | null;
};

/**
 * Attached to realtime toasts as sonner's `action` node. Deliberately offers
 * only three choices — pause, turn off everything, or go to settings — never
 * per-notification-type toggles, which belong to a considered settings visit
 * rather than a four-second reflex.
 */
export function ToastControlsMenu({ organizationId }: ToastControlsMenuProps) {
    const navigate = useNavigate();
    const { mutate: mutatePreferences } =
        useNotificationPreferences(organizationId);

    async function handleTurnOffAll() {
        if (!organizationId) {
            return;
        }
        try {
            await getApiClient().patch({
                url: '/users/me/notification-preferences',
                queryParams: { organization_id: organizationId },
                body: { toast_global_enabled: false },
            });
            await mutatePreferences();
            toast.success('Pop-ups turned off', {
                persistNotification: false,
                // Without this the confirmation is suppressed by the switch
                // it just flipped, taking the Undo button down with it.
                alwaysShow: true,
                description:
                    'You’ll still see everything in your notification bell.',
                action: {
                    label: 'Undo',
                    onClick: () => {
                        void (async () => {
                            await getApiClient().patch({
                                url: '/users/me/notification-preferences',
                                queryParams: {
                                    organization_id: organizationId,
                                },
                                body: { toast_global_enabled: true },
                            });
                            await mutatePreferences();
                        })();
                    },
                },
            });
        } catch {
            toast.error('Failed to turn off pop-ups', {
                persistNotification: false,
            });
        }
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild={true}>
                <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Pop-up options"
                    data-button={true}
                    className="text-muted-foreground hover:text-foreground h-7 w-7"
                >
                    <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                // Sonner's toast container paints at z-index 999999999 (see
                // its packaged styles.css), which sits above this menu's
                // default z-50 and swallows every pointer click. This must
                // stay higher than that value or the three items below
                // become unclickable with a real mouse again — keyboard
                // navigation and dispatched events don't hit-test, so this
                // regression won't show up in those checks.
                className="z-[1000000000]"
            >
                <DropdownMenuItem
                    onSelect={() => {
                        toastPauseStore.pauseForMs(TOAST_PAUSE_DURATION_MS);
                    }}
                >
                    Pause pop-ups for 1 hour
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => void handleTurnOffAll()}>
                    Turn off all pop-ups
                </DropdownMenuItem>
                <DropdownMenuItem
                    onSelect={() => {
                        void navigate({ to: '/settings/notifications' });
                    }}
                >
                    Notification settings…
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
